import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AdminSettings() {
  const [tab, setTab] = useState("paypal");
  const [s, setS] = useState({});

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async (key, body) => { await api.put(`/admin/settings/${key}`, body); toast.success("Settings saved."); setS({ ...s, [key]: { ...s[key], ...body } }); };

  const tabs = [["paypal", "Payments"], ["shipping", "Shipping"], ["tax", "Taxes"], ["duty", "Duties"], ["currency", "Currency"], ["stallion", "Stallion"], ["promo", "Bespoke Promo"]];
  const inp = "w-full h-10 px-3 border border-neutral-300 rounded text-sm";

  return (
    <div>
      <h1 className="font-serif text-3xl mb-6">Settings</h1>
      <div className="flex gap-2 mb-6 border-b border-neutral-200 flex-wrap">
        {tabs.map(([k, l]) => <button key={k} data-testid={`settings-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm ${tab === k ? "border-b-2 border-neutral-900 font-medium" : "text-neutral-500"}`}>{l}</button>)}
      </div>
      <div className="bg-white border border-neutral-200 rounded-lg p-6 max-w-2xl">
        {tab === "paypal" && <PayPal data={s.paypal || {}} save={save} inp={inp} />}
        {tab === "shipping" && <Shipping data={s.shipping || {}} save={save} inp={inp} />}
        {tab === "tax" && <Taxes data={s.tax || {}} save={save} inp={inp} />}
        {tab === "duty" && <Duty data={s.duty || {}} save={save} inp={inp} />}
        {tab === "currency" && <Currency data={s.currency || {}} save={save} inp={inp} />}
        {tab === "stallion" && <Stallion data={s.shipping || {}} save={save} inp={inp} />}
        {tab === "promo" && <Promo data={s.ring_promo || {}} save={save} inp={inp} />}
      </div>
    </div>
  );
}

function Note({ children }) { return <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded p-3 mb-4">{children}</div>; }

function PayPal({ data }) {
  return (<div>
    <h2 className="font-serif text-xl mb-4">PayPal Payments</h2>
    <Note><strong>Configuration required.</strong> PayPal credentials are stored securely server-side in <code>backend/.env</code> (never exposed to the browser). Provide <code>PAYPAL_CLIENT_ID</code> and <code>PAYPAL_SECRET</code> to accept live payments. Status: <strong>{data.configured ? "Configured" : "Not configured — orders are created with verified totals and marked pending."}</strong></Note>
    <p className="text-sm text-neutral-500">Checkout always re-prices server-side (product price, discount, shipping, tax, duties) before an order is created — browser prices are never trusted.</p>
  </div>);
}

function Shipping({ data, save, inp }) {
  const [free, setFree] = useState(data.free_threshold_usd ?? 500);
  const [rates, setRates] = useState(JSON.stringify(data.rates || {}, null, 2));
  return (<div>
    <h2 className="font-serif text-xl mb-4">Shipping</h2>
    <p className="text-sm text-neutral-500 mb-4">Origin: {data.origin || "Milton / Mississauga, Ontario, Canada"}</p>
    <label className="text-xs uppercase text-neutral-500">Free Shipping Threshold (USD)</label>
    <input type="number" className={`${inp} mb-4`} value={free} onChange={(e) => setFree(e.target.value)} />
    <label className="text-xs uppercase text-neutral-500">Rate Overrides (JSON by region: domestic, usa, europe, intl)</label>
    <textarea className={`${inp} h-40 py-2 font-mono text-xs mb-4`} value={rates} onChange={(e) => setRates(e.target.value)} />
    <button onClick={() => { try { save("shipping", { free_threshold_usd: Number(free), rates: JSON.parse(rates) }); } catch { toast.error("Invalid JSON"); } }} className="bg-neutral-900 text-white h-10 px-6 rounded text-sm">Save</button>
  </div>);
}

function Taxes({ data, save, inp }) {
  const [ca, setCa] = useState(JSON.stringify(data.ca_rates || {}, null, 2));
  const [intl, setIntl] = useState(JSON.stringify(data.intl_rates || {}, null, 2));
  return (<div>
    <h2 className="font-serif text-xl mb-4">Taxes</h2>
    <p className="text-sm text-neutral-500 mb-4">Canadian sales tax is applied by shipping province (not a flat rate). International tax applied by destination. Override defaults below.</p>
    <label className="text-xs uppercase text-neutral-500">Canadian Province Overrides (e.g. {`{"ON":0.13}`})</label>
    <textarea className={`${inp} h-28 py-2 font-mono text-xs mb-3`} value={ca} onChange={(e) => setCa(e.target.value)} />
    <label className="text-xs uppercase text-neutral-500">International Overrides (by country code)</label>
    <textarea className={`${inp} h-28 py-2 font-mono text-xs mb-4`} value={intl} onChange={(e) => setIntl(e.target.value)} />
    <button onClick={() => { try { save("tax", { ca_rates: JSON.parse(ca), intl_rates: JSON.parse(intl) }); } catch { toast.error("Invalid JSON"); } }} className="bg-neutral-900 text-white h-10 px-6 rounded text-sm">Save</button>
  </div>);
}

function Duty({ data, save, inp }) {
  const [rates, setRates] = useState(JSON.stringify(data.rates || {}, null, 2));
  return (<div>
    <h2 className="font-serif text-xl mb-4">Duties & Import Taxes</h2>
    <Note>Duties are estimated and collected upfront where possible, using country, product value, HS code and origin. Live duty APIs take priority; this configurable table and AI act as fallback layers. Estimated amounts are clearly labelled at checkout.</Note>
    <label className="text-xs uppercase text-neutral-500">Duty Rate Overrides (by country code, plus "default")</label>
    <textarea className={`${inp} h-32 py-2 font-mono text-xs mb-4`} value={rates} onChange={(e) => setRates(e.target.value)} />
    <button onClick={() => { try { save("duty", { rates: JSON.parse(rates) }); } catch { toast.error("Invalid JSON"); } }} className="bg-neutral-900 text-white h-10 px-6 rounded text-sm">Save</button>
  </div>);
}

function Currency({ data, save, inp }) {
  const [ov, setOv] = useState(JSON.stringify(data.overrides || {}, null, 2));
  return (<div>
    <h2 className="font-serif text-xl mb-4">Currency</h2>
    <p className="text-sm text-neutral-500 mb-4">Live exchange rates are fetched automatically (USD base) and cached. Base product prices are stored in USD; customer-facing prices are converted live. Add manual overrides below.</p>
    <p className="text-xs text-neutral-400 mb-4">Last fetch: {data.last_fetch || "—"}</p>
    <label className="text-xs uppercase text-neutral-500">Manual Rate Overrides (e.g. {`{"CAD":1.38}`})</label>
    <textarea className={`${inp} h-32 py-2 font-mono text-xs mb-4`} value={ov} onChange={(e) => setOv(e.target.value)} />
    <button onClick={() => { try { save("currency", { overrides: JSON.parse(ov) }); } catch { toast.error("Invalid JSON"); } }} className="bg-neutral-900 text-white h-10 px-6 rounded text-sm">Save</button>
  </div>);
}

function Promo({ data, save, inp }) {
  const [f, setF] = useState(data || {});
  const set = (k, v) => setF({ ...f, [k]: v });
  return (<div>
    <h2 className="font-serif text-xl mb-4">Bespoke Ring Promotional Panel</h2>
    <label className="flex items-center gap-2 text-sm mb-4"><input type="checkbox" checked={f.enabled !== false} onChange={(e) => set("enabled", e.target.checked)} /> Feature enabled (shown on Engagement Rings page)</label>
    <label className="text-xs uppercase text-neutral-500">Heading</label>
    <input data-testid="promo-heading" className={`${inp} mb-3`} value={f.heading || ""} onChange={(e) => set("heading", e.target.value)} />
    <label className="text-xs uppercase text-neutral-500">Description</label>
    <textarea className={`${inp} h-24 py-2 mb-3`} value={f.description || ""} onChange={(e) => set("description", e.target.value)} />
    <label className="text-xs uppercase text-neutral-500">Button Text</label>
    <input className={`${inp} mb-3`} value={f.button_text || ""} onChange={(e) => set("button_text", e.target.value)} />
    <label className="text-xs uppercase text-neutral-500">Optional Promo Image URL</label>
    <input className={`${inp} mb-3`} value={f.image || ""} onChange={(e) => set("image", e.target.value)} />
    <label className="text-xs uppercase text-neutral-500">Confirmation Message</label>
    <textarea className={`${inp} h-20 py-2 mb-3`} value={f.confirmation || ""} onChange={(e) => set("confirmation", e.target.value)} />
    <label className="text-xs uppercase text-neutral-500">Budget Ranges (one per line, blank = show min/max fields)</label>
    <textarea className={`${inp} h-24 py-2 mb-4`} value={(f.budget_ranges || []).join("\n")} onChange={(e) => set("budget_ranges", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} />
    <button data-testid="promo-save" onClick={() => save("ring_promo", { enabled: f.enabled !== false, heading: f.heading, description: f.description, button_text: f.button_text, image: f.image, confirmation: f.confirmation, budget_ranges: f.budget_ranges || [] })} className="bg-neutral-900 text-white h-10 px-6 rounded text-sm">Save</button>
  </div>);
}

function Stallion({ data, save, inp }) {
  return (<div>
    <h2 className="font-serif text-xl mb-4">Stallion Express Integration</h2>
    <Note><strong>Integration point ready.</strong> Stallion is the preferred carrier (shipments originate in Ontario). Provide <code>STALLION_API_KEY</code> in <code>backend/.env</code> to enable live rate lookup, label generation and tracking. Until configured, the system uses the configurable fallback rate table and clearly labels rates as estimates.</Note>
    <p className="text-sm text-neutral-500">Capabilities when connected: rate lookup, label generation, tracking, shipment creation, order sync. Rates are never hard-coded.</p>
  </div>);
}
